import type React from 'react';
import { useState } from 'react';

import { DetailsBooksItems, DetailsBooksResponse } from '@/app/models/detailsBooks';
import { useUndoUnionBookMutation } from '@/app/services/detailsBooksApi';
import { formatDate } from '@/app/utils/dateUtils';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import { usePeriod } from '@/context/PeriodContext';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LinkIcon from '@mui/icons-material/Link';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Checkbox, IconButton, Menu, MenuItem, TableCell, Tooltip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import TruncatedTextWithTooltip from '../../common/truncatedTextWithTooltip';

interface BooksTableProps {
  selectBook: number[];
  setSelectBook: (id: number[]) => void;
  setDrawerEdit: CallableFunction;
  setDrawerTransfer: CallableFunction;
  setSelectedBookItem: (id: DetailsBooksItems) => void;
  selectBooksItem: DetailsBooksItems;
  bookData: DetailsBooksResponse | undefined;
}

export default function BooksTable({
  selectBook,
  setSelectBook,
  setDrawerEdit,
  setDrawerTransfer,
  setSelectedBookItem,
  selectBooksItem,
  bookData,
}: BooksTableProps) {
  const { t } = useTranslation(['books', 'common']);
  const theme = useTheme();
  const { selectedPeriod } = usePeriod();

  const [selectedStatus, setSelectedStatus] = useState<number | null>(null);
  const [idUndoUnion, setIdUndoUnion] = useState<number | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleCheckboxChange = (id: number, checked: boolean) => {
    const newSelectedCollaborators = checked ? [...selectBook, id] : selectBook.filter((collabId) => collabId !== id);

    setSelectBook(newSelectedCollaborators);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = bookData?.items?.map((collab) => collab.id);
      setSelectBook(allIds as number[]);
    } else {
      setSelectBook([]);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedStatus(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrowersEdit = () => {
    setDrawerEdit(true);
    setAnchorEl(null);
  };

  const handleDrowersTrasnfer = () => {
    setDrawerTransfer(true);
    setAnchorEl(null);
  };

  const [undoUnion] = useUndoUnionBookMutation();

  const handleUndoUnion = async () => {
    await toast.promise(
      undoUnion({
        id: idUndoUnion as number,
        period: selectedPeriod?.date as string,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:undo.union.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setDrawerEdit(false);
    setAnchorEl(null);
  };

  const columns: Column[] = [
    {
      key: 'select',
      label: (
        <Checkbox
          color="secondary"
          sx={{ p: 0 }}
          indeterminate={selectBook.length > 0 && selectBook.length < (bookData?.items?.length as number)}
          checked={(bookData?.items?.length as number) > 0 && selectBook.length === bookData?.items?.length}
          onChange={handleSelectAll}
          inputProps={{ 'aria-label': 'select all collaborators' }}
        />
      ),
      width: theme.spacing(6),
    },
    { key: 'title', label: t('title'), width: theme.spacing(30) },
    { key: 'quantity', label: t('common:quantity'), align: 'center' },
    { key: 'total.value', label: t('common:total.value') },
    { key: 'expenseDate', label: t('common:expenseDate') },
    { key: 'justification', label: t('justification'), width: theme.spacing(60) },
    { key: 'consolidated', label: t('common:consolidated'), align: 'center' },
    { key: 'grouped', label: t('common:grouped'), align: 'center' },
    { key: 'actions', label: t('common:actions'), align: 'right' },
  ];

  const renderRow = (item: DetailsBooksItems) => {
    const isSelected = selectBook.includes(item.id);
    return (
      <>
        <TableCell sx={{ ...bodyCellSx, padding: 'checkbox' }}>
          <Checkbox
            color="secondary"
            sx={{ p: 0 }}
            checked={isSelected}
            onChange={(e) => handleCheckboxChange(item.id, e.target.checked)}
            inputProps={{ 'aria-labelledby': `Item-${item.id}` }}
          />
        </TableCell>

        <TableCell sx={bodyCellSx}>
          <TruncatedTextWithTooltip
            value={`${item.description ? item.description : '---'}`}
            sx={bodyCellSx}
            size={40}
          />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>{item.quantity}</TableCell>

        <TableCell sx={{ ...bodyCellSx }}>{formatterMonetaryValue(item.value)}</TableCell>
        <TableCell sx={{ ...bodyCellSx }}>{item.expenseDate ? formatDate(item.expenseDate) : '----'}</TableCell>

        <TableCell sx={{ ...bodyCellSx }}>
          <TruncatedTextWithTooltip value={`${item.justify ? item.justify : '----'}`} sx={bodyCellSx} size={45} />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
          {item.consolidated ? (
            <CheckIcon sx={{ color: theme.palette.success.light, cursor: 'pointer' }} />
          ) : (
            <CloseIcon sx={{ color: theme.palette.grey[500], cursor: 'pointer' }} />
          )}
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
          {item.grouped ? (
            <Tooltip title={t('common:union.result')} arrow placement="bottom-start">
              <LinkIcon sx={{ color: theme.palette.secondary.light, cursor: 'pointer' }} />
            </Tooltip>
          ) : null}
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
          <IconButton
            size="small"
            onClick={(e) => {
              handleMenuOpen(e, item.id);
              setSelectedBookItem(item);
              setIdUndoUnion(item.id);
            }}
            aria-label="more actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </>
    );
  };

  // Use the project ID as the unique key
  const getRowKey = (project: DetailsBooksItems) => project.id as number;

  return (
    <>
      <DataTable
        columns={columns}
        data={bookData?.items || []}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
        size="small"
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedStatus && (
          <>
            <MenuItem onClick={() => handleDrowersEdit()}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ListAltIcon />
                {t('common:edit.detail')}
              </Box>
            </MenuItem>
            <MenuItem onClick={() => handleDrowersTrasnfer()}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompareArrowsIcon />
                {t('common:transfer')}
              </Box>
            </MenuItem>
            {selectBooksItem.grouped && (
              <MenuItem onClick={() => handleUndoUnion()}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CallSplitIcon />
                  {t('common:undo.union')}
                </Box>
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </>
  );
}
